<template>
  <div class="contents koyoukeiyaku">
    <h2>本人承認済み雇用契約の修正</h2>
    <div class="information">
      <p>
        この画面では本人承認済みの雇用契約について一部項目の修正を行えます。<br>
        ここで保存した内容は承認ルートを経由せず即時反映されます。<br>
        なお、ここで変更した内容はこれ以後の打刻や申請等へのみの適用となり、<br>過去のデータへの適用は行われない点にご注意ください。
      </p>
    </div>
    <div class="container edit-page" :class="'mode-modify'">
      <loading v-if="isLoading && !isError" :message="'処理中...'"></loading>
      <p v-else-if="isError">画面の初期化に失敗しました</p>

      <div class="select-sheet" v-else>
        <div class="edit-sheet">
          <h3><i class="fa-regular fa-file-lines"></i>{{ sheetTitle }}</h3>
          <modify :editingData="editingKoyouKeiyaku" :currentData="keiyakuDetail"
            :mode="OptionConst.KOYOUKEIYAKU_EDIT_MODE.MODIFY" />
        </div>
      </div><!--container-->

      <div class="button-container">
        <button type="button" class="btn danger" @click="exit()" :disabled="isLoading">
          保存せずに終了
        </button>

        <button type="button" class="btn success" @click="modify()">
          <i class="fa-solid fa-file-lines"></i>変更内容を保存
        </button>
      </div><!--button-container-->
    </div>
  </div><!--contents-->
</template>

<script lang="ts">

import { defineComponent, ref, onMounted, Ref } from 'vue'
import { useKoyouKeiyakuInit } from '@/composable/koyoukeiyaku/init';
import OptionConst from '@/const/Option';
import { RouterConst } from '@/const/Router';
import { useRoute, useRouter } from 'vue-router';
import { useKoyouKeiyakuDetail } from '@/composable/koyoukeiyaku/detail';
import { cloneDeep } from 'lodash';
import Modify from '@/components/koyoukeiyaku/Modify.vue';
import { DialogService } from '@/service/DialogService';
import { KoyouKeiyakuDetail } from '@/types/KoyouKeiyakuDetail';
import { useKoyouKeiyakuModify } from '@/composable/koyoukeiyaku/detail/modify';

export default defineComponent({
  setup() {
    const { isError, options, shinseiTypeOptionsForCreate, setKoyouKeiyakuTemplate, sheetTitle } = useKoyouKeiyakuInit()
    const { keiyakuDetail, getKeiyakuDetail } = useKoyouKeiyakuDetail()
    const { modifyKoyouKeiyaku } = useKoyouKeiyakuModify()

    const prevPage = ref({
      name: RouterConst.KOYOUKEIYAKU_MENU,
      params: {}
    })
    const prevPageLabel = ref('雇用契約メニューに戻る')

    // 作成モードと雇用契約IDを取得（新規作成 / 編集）
    const router = useRouter()
    const route = useRoute();
    const keiyakuId = parseInt(route.params.id as string, 10)

    // 描画のための初期化済フラグ
    const isLoading = ref(false)

    // TODO: デバッグ用
    const showDebugData = ref(false)

    const editingKoyouKeiyaku: Ref<KoyouKeiyakuDetail | null> = ref(null)

    onMounted(async () => {
      isLoading.value = true
      if (!keiyakuId) {
        DialogService.error({
          text: '雇用契約IDが指定されていません。'
        })
        router.go(-1)
      }
      prevPage.value = {
        name: RouterConst.KOYOUKEIYAKU_DETAIL,
        params: {
          id: keiyakuId
        }
      }
      prevPageLabel.value = '詳細画面に戻る'

      await load()
      isLoading.value = false
    })

    const load = async () => {
      await getKeiyakuDetail(keiyakuId)
      if (!keiyakuDetail.value) {
        DialogService.error({
          text: '雇用契約の取得に失敗しました。前の画面へ戻ります'
        })
        // TODO: 直アクセスの場合を考慮する
        router.go(-1)
        return
      }
      editingKoyouKeiyaku.value = cloneDeep(keiyakuDetail.value);

      // 雇用契約データをもとにテンプレートを決定する
      await setKoyouKeiyakuTemplate(editingKoyouKeiyaku.value);
    }

    const exit = async () => {
      router.push(prevPage.value)
    }

    const modify = async () => {
      if (await modifyKoyouKeiyaku(editingKoyouKeiyaku.value)) {        
        await DialogService.info({
          html: "更新に成功しました。再読込します。"
        })
        isLoading.value = true
        await load()
      }
      isLoading.value = false
    }

    return {
      showDebugData,
      isLoading,
      isError,
      options,
      sheetTitle,
      RouterConst,
      OptionConst,
      shinseiTypeOptionsForCreate,
      keiyakuId,
      keiyakuDetail,
      prevPage,
      prevPageLabel,
      exit,
      modify,
      editingKoyouKeiyaku,
    }
  },
  components: {
    'modify': Modify,  // 共通テンプレート
  }
})

</script>

<style lang="scss" scoped>
@import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/koyoukeiyaku-edit.scss';
</style>
